"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import { ChevronRight } from "lucide-react";
import Link from "next/link";
import {
  useMemo,
  useRef,
  type ReactNode,
} from "react";

import { PAGE_TYPES } from "../../../constants";

interface BreadcrumbMeta {
  id?: string;
  meta_label?: string;
  meta_url?: string;
  meta_priority?: number;
  meta_status?: boolean | number | string;
}

const metaVisible = (item: BreadcrumbMeta) =>
  item.meta_status === true ||
  item.meta_status === 1 ||
  item.meta_status === "1";

export default function BreadcrumbBanner1({
  data,
  pageCategory,
}: {
  data: any;
  pageCategory?: string;
}) {
  const ref = useRef<HTMLElement>(null);
  const sectionData = (data as any)?.data ?? data;

  const isBlogLayout =
    String(pageCategory ?? "").toLowerCase() ===
    PAGE_TYPES.BLOG.toLowerCase();

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "breadcrumb_section");
  //   }
  // }, []);

  const {
    breadcrumb_strip_background_color = "#F4F4F4",
    link_text_color = "#666666",
    current_text_color = "#943126",
    separator_color = "#737373",
    metadata = [],
  } = sectionData || {};

  const crumbs = useMemo(() => {
    if (!Array.isArray(metadata)) return [];
    return [...metadata]
      .filter(metaVisible)
      .sort(
        (a: BreadcrumbMeta, b: BreadcrumbMeta) =>
          (a.meta_priority ?? 0) - (b.meta_priority ?? 0)
      )
      .filter((c: BreadcrumbMeta) => String(c.meta_label ?? "").trim());
  }, [metadata]);

  const isActive =
    sectionData?.status === true ||
    sectionData?.status === 1 ||
    sectionData?.status === "1";

  if (!sectionData || !isActive || crumbs.length === 0) {
    return null;
  }

  const renderCrumbLink = (
    href: string,
    children: ReactNode,
    className?: string
  ) => {
    const trimmed = href.trim();
    if (!trimmed) {
      return (
        <span
          className={cn(
            "inline-flex max-w-[85vw] truncate md:max-w-none",
            className
          )}
        >
          {children}
        </span>
      );
    }
    if (/^https?:\/\//i.test(trimmed)) {
      return (
        <a
          href={trimmed}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "inline-flex max-w-[85vw] truncate underline-offset-2 hover:underline md:max-w-none",
            className
          )}
        >
          {children}
        </a>
      );
    }
    return (
      <Link
        href={trimmed.startsWith("/") ? trimmed : `/${trimmed}`}
        className={cn(
          "inline-flex max-w-[85vw] truncate underline-offset-2 hover:underline md:max-w-none",
          className
        )}
      >
        {children}
      </Link>
    );
  };

  return (
    <section
      ref={ref}
      className={cn("w-full", isBlogLayout && "min-w-0")}
    >
      <nav
        aria-label="Breadcrumb"
        className="w-full "
        // style={{ backgroundColor: breadcrumb_strip_background_color }}
      >
        <div
          className={cn(
            "scrollbar-hide overflow-x-auto overscroll-x-contain py-3",
            isBlogLayout
              && "px-0"
             
          )}
        >
          <ol className="flex min-w-min flex-nowrap items-center gap-x-1.5 gap-y-1 text-sm leading-snug md:text-[15px]">
            {crumbs.map((crumb: BreadcrumbMeta, index: number) => {
              const isLast = index === crumbs.length - 1;
              const label = String(crumb.meta_label ?? "").trim();
              const href = String(crumb.meta_url ?? "").trim();

              const crumbBody =
                isLast || !href ? (
                  <span
                    aria-current={isLast ? "page" : undefined}
                    className={cn(
                      "inline-flex max-w-[85vw] shrink-0 whitespace-nowrap md:max-w-none",
                      isLast ? "font-medium" : "font-normal"
                    )}
                    style={{
                      color: isLast ? current_text_color : link_text_color,
                    }}
                  >
                    {label}
                  </span>
                ) : (
                  <span style={{ color: link_text_color }}>
                    {renderCrumbLink(
                      href,
                      label,
                      "font-normal shrink-0 whitespace-nowrap"
                    )}
                  </span>
                );

              return (
                <li
                  key={`${crumb.id ?? label}-${index}`}
                  className="flex shrink-0 items-center gap-x-1.5"
                >
                  {index > 0 ? (
                    <ChevronRight
                      className="size-[14px] shrink-0 opacity-90"
                      style={{ color: separator_color }}
                      aria-hidden
                    />
                  ) : null}
                  {crumbBody}
                </li>
              );
            })}
          </ol>
        </div>
      </nav>
    </section>
  );
}
